import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  OnInit,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface FolderTreeNode {
  path: string | null;
  name: string;
  subFolders?: FolderTreeNode[];
}

@Component({
  selector: 'sh-folder-tree-table',
  templateUrl: './folder-tree-table.component.html',
  standalone: true,
  imports: [CommonModule],
})
export class FolderTreeTableComponent implements OnInit {
  private _translateService = inject(TranslateService);

  private _folders: FolderTreeNode[] = [];

  @Input() set folders(folders: FolderTreeNode[]) {
    this._folders = folders || [];
    this.setUpFolders();
  }
  get folders(): FolderTreeNode[] {
    return this._folders;
  }

  @Input() selectedPath: string | null = null;
  @Input() showRoot = true;
  @Input() showNewSubPathButton = false;
  @Input() rootLabel = this._translateService.instant('shared.terms.all');
  @Input() idPrefix = 'folderTreePath';
  @Input() folderContentTemplate?: TemplateRef<unknown>;
  @Input() projectedColumns = 1;

  collapsed: { [key: string]: boolean } = {};
  displayFolders: FolderTreeNode[] = [];

  @Output() selectPath = new EventEmitter<FolderTreeNode | null>();
  @Output() newSubPath = new EventEmitter<string>();
  @Output() openPaths = new EventEmitter<FolderTreeNode[] | null>();

  ngOnInit() {
    this.setUpFolders();
  }

  private setUpFolders() {
    if (this.showRoot) {
      this.displayFolders = [
        {
          path: '__root',
          name: this.rootLabel,
          subFolders: this.folders,
        },
      ];
    } else {
      this.displayFolders = this.folders;
    }
    this.emitOpenNodes();
  }

  onSelectPath(path: FolderTreeNode | null) {
    this.selectPath.emit(path);
  }

  emitNewSubPath(path: FolderTreeNode) {
    this.newSubPath.emit(path.path + '/');
  }

  isCollapsed(key: string): boolean {
    return !!this.collapsed[key];
  }

  toggleCollapse(key: string, event?: Event) {
    this.collapsed[key] = !this.collapsed[key];

    if (event) {
      event.stopPropagation();
    }

    this.emitOpenNodes();
  }

  isAnyAncestorCollapsed(ancestorKeys: string[]): boolean {
    return ancestorKeys?.some((key) => this.isCollapsed(key)) || false;
  }

  emitOpenNodes() {
    const openNodes: FolderTreeNode[] = [];
    const traverse = (
      nodes: FolderTreeNode[],
      parentKey: string,
      level: number
    ) => {
      nodes.forEach((node, i) => {
        const rowKey = `${parentKey}_colLevel${level}-${i}`;
        if (!this.isCollapsed(rowKey)) {
          openNodes.push(node);
          if (node.subFolders && node.subFolders.length > 0) {
            traverse(node.subFolders, rowKey, level + 1);
          }
        }
      });
    };

    const rootKey = '__root';
    const nodes = this.showRoot
      ? [{ path: '__root', name: this.rootLabel, subFolders: this.folders }]
      : this.folders;

    traverse(nodes, rootKey, 1);
    this.openPaths.emit(openNodes);
  }
}
